import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye, Package, Image, File, Maximize2, Filter, Download } from 'lucide-react';
import { useProducts, useDeleteProduct } from '../../hooks/useProducts';
import { useDebounce } from '../../hooks/useDebounce';
import { productsApi } from '../../api/products';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';

type FilterStatus = 'todos' | 'activo' | 'inactivo';

export function ProductList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('todos');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewProduct, setPreviewProduct] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const setPage = (newPage: number) => {
    setSearchParams((prev) => {
      prev.set('page', newPage.toString());
      return prev;
    });
  };

  const itemsPerPage = 20;
  const debouncedSearch = useDebounce(searchTerm, 400);

  const getActivoFilter = (): boolean | null => {
    if (statusFilter === 'todos') return null;
    return statusFilter === 'activo';
  };

  const { data: paginationData, isLoading, isError, refetch } = useProducts({
    activo: getActivoFilter(),
    search: debouncedSearch,
    page,
    limit: itemsPerPage,
  });

  const products = paginationData?.data || [];
  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  const deleteProduct = useDeleteProduct();

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleStatusChange = (status: FilterStatus) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleExportInventario = async () => {
    try {
      setExporting(true);
      toast.info('Generando PDF de inventario...');

      const response = await productsApi.exportInventario();

      if (response.success) {
        toast.success('PDF generado exitosamente');
        window.open(response.pdfUrl, '_blank');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al generar el PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id: number, nombre: string) => {
    if (!confirm(`¿Estás seguro de eliminar el producto "${nombre}"?`)) return;

    try {
      await deleteProduct.mutateAsync(id);
      toast.success('Producto eliminado exitosamente');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el producto');
    }
  };

  const openPreview = (imageUrl: string, productName: string) => {
    setPreviewImage(imageUrl);
    setPreviewProduct(productName);
  };

  const closePreview = () => {
    setPreviewImage(null);
    setPreviewProduct('');
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Productos</h1>
          <p className="text-sm text-gray-500">
            {totalItems} producto(s) encontrados
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={handleExportInventario}
            disabled={exporting || totalItems === 0}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white flex justify-center items-center"
          >
            <Download size={18} className="mr-2" />
            {exporting ? 'Generando...' : 'Exportar PDF'}
          </Button>
          <Link to="/productos/nuevo" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white flex justify-center items-center">
              <Plus size={18} className="mr-2" />
              Nuevo Producto
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar productos por nombre, SKU o código de barras..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 sm:min-w-[180px]">
          <Filter size={16} className="text-gray-400 flex-shrink-0" />
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value as FilterStatus)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
          >
            <option value="todos">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>
      </div>

      <Card className="p-0 sm:p-4 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Cargando productos...</div>
          </div>
        ) : isError ? (
          <div className="text-center py-12 text-red-500">
            Error al cargar los productos
          </div>
        ) : (
          <>
            <div className="overflow-x-auto w-full">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 w-[80px]">Imagen</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Producto</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Categoría</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Precio</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Stock</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Archivos</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Estado</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product: any) => {
                    const hasImage = !!product.imagenUrl;
                    const hasPdf = !!product.documentoPdfUrl;

                    return (
                      <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          {hasImage ? (
                            <div className="relative group">
                              <img
                                src={product.imagenUrl}
                                alt={product.nombre}
                                className="w-14 h-14 object-cover rounded-lg border border-gray-200 cursor-pointer"
                                onClick={() => openPreview(product.imagenUrl, product.nombre)}
                              />
                              <button
                                onClick={() => openPreview(product.imagenUrl, product.nombre)}
                                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center"
                                title="Ver imagen ampliada"
                              >
                                <Maximize2 size={16} className="text-white" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 border border-gray-200">
                              <Package size={24} />
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-800">{product.nombre}</p>
                            {product.sku && (
                              <p className="text-xs text-gray-400">SKU: {product.sku}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {product.categoriaRel?.nombre || product.categoria || 'Sin categoría'}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-semibold text-gray-800">
                          {formatCurrency(product.precio)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            product.stock <= 0
                              ? 'bg-red-100 text-red-700'
                              : product.stock <= product.stockMinimo
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {product.stock} unidades
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {hasImage && (
                              <div className="flex items-center gap-1 text-blue-500" title="Tiene imagen">
                                <Image size={16} />
                                <span className="text-xs">Img</span>
                              </div>
                            )}
                            {hasPdf && (
                              <div className="flex items-center gap-1 text-red-500" title="Tiene PDF">
                                <File size={16} />
                                <span className="text-xs">PDF</span>
                              </div>
                            )}
                            {!hasImage && !hasPdf && (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            product.activo
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {product.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Link
                              to={`/productos/${product.id}?returnPage=${page}`}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Eye size={16} />
                            </Link>
                            <Link
                              to={`/productos/editar/${product.id}?returnPage=${page}`}
                              className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                            >
                              <Edit size={16} />
                            </Link>
                            <button
                              onClick={() => handleDelete(product.id, product.nombre)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {products.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <p>No se encontraron productos</p>
                <p className="text-sm mt-1">
                  {searchTerm
                    ? `No hay productos que coincidan con "${searchTerm}"`
                    : statusFilter !== 'todos'
                      ? `No hay productos ${statusFilter === 'activo' ? 'activos' : 'inactivos'}`
                      : 'No hay productos disponibles'
                  }
                </p>
              </div>
            )}

            {totalItems > 0 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        isOpen={!!previewImage}
        onClose={closePreview}
        title={previewProduct}
        size="lg"
      >
        <div className="flex items-center justify-center min-h-[300px]">
          {previewImage && (
            <img
              src={previewImage}
              alt={previewProduct}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-image.png';
              }}
            />
          )}
        </div>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">{previewProduct}</p>
          <button
            onClick={() => {
              if (previewImage) {
                window.open(previewImage, '_blank');
              }
            }}
            className="mt-2 text-sm text-blue-600 hover:underline"
          >
            Abrir imagen en nueva pestaña
          </button>
        </div>
      </Modal>
    </div>
  );
}